// FeatureErrorBoundary.jsx — per-feature boundaries with contextual fallbacks.
//
// TARGET PATH: src/shared/components/FeatureErrorBoundary.jsx
//
// Replicated from ecoh-ui-module's shared/components/FeatureErrorBoundary.jsx.
//
// The point of these over a single app-wide boundary is isolation and wording. A
// crash in Alerts should say "Alerts Unavailable" and offer a route back to the
// dashboard, not replace the whole application with a generic error — the
// sidebar and every other feature are still perfectly usable.
//
// Three boundaries rather than the reference's seven, because this app has three
// feature areas worth isolating. Adding one is four lines; there is no need to
// pre-create them for features that do not exist yet.
//
// Deviations from the reference, both forced by this repo's dependencies:
// icons are inline SVG (no lucide-react) and there are no PropTypes (not a
// dependency — and note the reference imports prop-types without declaring it).

import React from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import './ErrorBoundary.css';

/* ── Icons ─────────────────────────────────────────────────── */
const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: '2',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
};

function BellIcon({ size = 40 }) {
  return (
    <svg {...iconProps} width={size} height={size}>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function AlertTriangleIcon({ size = 40 }) {
  return (
    <svg {...iconProps} width={size} height={size}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function GridIcon({ size = 40 }) {
  return (
    <svg {...iconProps} width={size} height={size}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function RefreshIcon({ size = 18 }) {
  return (
    <svg {...iconProps} width={size} height={size}>
      <path d="M23 4v6h-6" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}

function HomeIcon({ size = 18 }) {
  return (
    <svg {...iconProps} width={size} height={size}>
      <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
      <path d="M9 21v-8h6v8" />
    </svg>
  );
}

/* ── Shared fallback ───────────────────────────────────────── */
/**
 * The screen a feature shows when it has crashed.
 *
 * The stack is rendered only outside production. In production it would tell the
 * user nothing they can act on; in development it saves a trip to the console.
 */
const FeatureErrorFallback = ({
  featureName,
  icon: Icon,
  error,
  onRetry,
  homeRoute = '/dashboard',
  description,
}) => {
  const navigate = useNavigate();

  const isChunkError =
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Failed to fetch') ||
    error?.name === 'ChunkLoadError';

  const errorMessage = isChunkError
    ? `Failed to load ${featureName}. This might be due to a network issue or an app update.`
    : `An error occurred in ${featureName}.`;

  return (
    <div className="eb-feature" role="alert">
      <div className="eb-feature-icon">
        <Icon size={40} />
      </div>

      <h2 className="eb-feature-title">{featureName} Unavailable</h2>

      <p className="eb-feature-message">{errorMessage}</p>

      {description && <p className="eb-feature-description">{description}</p>}

      <div className="eb-feature-actions">
        <button type="button" className="eb-btn eb-btn-primary" onClick={onRetry}>
          <RefreshIcon />
          Try Again
        </button>

        {homeRoute && (
          <button
            type="button"
            className="eb-btn eb-btn-secondary"
            onClick={() => navigate(homeRoute)}
          >
            <HomeIcon />
            Go to Dashboard
          </button>
        )}
      </div>

      {process.env.NODE_ENV === 'development' && error && (
        <details className="eb-details">
          <summary>Error Details (Development Only)</summary>
          <pre>
            {error.toString()}
            {error.stack && `\n\n${error.stack}`}
          </pre>
        </details>
      )}
    </div>
  );
};

/* ── Feature boundaries ────────────────────────────────────── */
export const AlertsErrorBoundary = ({ children }) => (
  <ErrorBoundary
    componentName="Alerts"
    fallback={({ error, retry }) => (
      <FeatureErrorFallback
        featureName="Alerts"
        icon={BellIcon}
        error={error}
        onRetry={retry}
        description="Alerts are temporarily unavailable. Nothing has been lost — the alert itself is unaffected."
      />
    )}
  >
    {children}
  </ErrorBoundary>
);

export const IncidentsErrorBoundary = ({ children }) => (
  <ErrorBoundary
    componentName="Incidents"
    fallback={({ error, retry }) => (
      <FeatureErrorFallback
        featureName="Incidents"
        icon={AlertTriangleIcon}
        error={error}
        onRetry={retry}
        description="Incident management is temporarily unavailable. Your incidents are safe."
      />
    )}
  >
    {children}
  </ErrorBoundary>
);

export const DashboardErrorBoundary = ({ children }) => (
  <ErrorBoundary
    componentName="Dashboard"
    fallback={({ error, retry }) => (
      <FeatureErrorFallback
        featureName="Dashboard"
        icon={GridIcon}
        error={error}
        onRetry={retry}
        description="You can try reloading the dashboard, or use the sidebar to go elsewhere."
      />
    )}
  >
    {children}
  </ErrorBoundary>
);

export { ErrorBoundary, FeatureErrorFallback };
export default ErrorBoundary;
